import { useState, useEffect } from 'react';
import { palette } from '../../styles/tokens';
import { DatePicker } from '../common/DatePicker';
import { useCreateTimeOff } from '../../hooks/useCreateTimeOff';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';

interface Props { onCreated: () => void; onCancel: () => void; }
interface TeamUser { id: string; first_name: string; last_name: string; }

const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' };
const INPUT: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };
const FIELD: React.CSSProperties = { marginBottom: 16 };

export function CreateTimeOffForm({ onCreated, onCancel }: Props) {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const { createTimeOff, loading, error } = useCreateTimeOff();
  const [users, setUsers] = useState<TeamUser[]>([]);

  const [userId, setUserId] = useState('');
  const [type, setType] = useState('personal');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!business) return;
    supabase.from('users').select('id, first_name, last_name').eq('business_id', business.id).eq('status', 'active')
      .then(({ data }) => setUsers(data ?? []));
  }, [business]);

  const handleSubmit = async () => {
    if (!business || !user) return;
    const ok = await createTimeOff({
      business_id: business.id, user_id: userId, date: startDate,
      end_date: endDate || undefined, type, reason, approved_by: user.id,
    });
    if (ok) onCreated();
  };

  return (
    <div>
      {error && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{error}</div>}
      <div style={FIELD}><label style={LABEL}>Team member *</label><select style={INPUT} value={userId} onChange={e => setUserId(e.target.value)}><option value="">Select...</option>{users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}</select></div>
      <div style={FIELD}><label style={LABEL}>Type</label><div style={{ display: 'flex', gap: 16 }}>
        {['vacation', 'sick', 'personal', 'other'].map(t => (
          <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13, textTransform: 'capitalize' }}><input type="radio" name="type" checked={type === t} onChange={() => setType(t)} />{t}</label>
        ))}
      </div></div>
      <div style={{ display: 'flex', gap: 12, ...FIELD }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Start date *</label>
          <DatePicker value={startDate || null} onChange={(v) => setStartDate(v ?? '')} />
        </div>
        <div style={{ flex: 1 }}><label style={LABEL}>End date</label>
          <DatePicker value={endDate || null} onChange={(v) => setEndDate(v ?? '')} minDate={startDate || undefined} placeholder="Same day if blank" />
        </div>
      </div>
      <div style={FIELD}><label style={LABEL}>Reason</label><textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={reason} onChange={e => setReason(e.target.value)} /></div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={onCancel} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={handleSubmit} disabled={loading} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: loading ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{loading ? 'Creating...' : 'Create Time Off'}</button>
      </div>
    </div>
  );
}
